import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import TaskTable from "./task-table";
import TaskCard from "./task-card/task-card";
import ApiTaskManagement from "../../../api/task-managements";
import ApiCohortManagement from "../../../api/cohort-management";
import useNotification from "../../../components/notification/notification";
import { isReferencedError, hasReferencedFailure } from "../../../common/utils/referenced-error";
import "./styles.css";
import { Body } from "../../../components/main-body/body";
import { Modal } from "antd";
import { useParams, useLocation, useOutletContext, useSearchParams } from "react-router-dom";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import DropdownFilters from "../../../components/dropdown-filters/dropdown-filters";
import { CREATED_VIA_FILTER } from "../../../components/table/ai-draft-label";
import debounce from "../../../common/utils/debounce";
import VersionPage from "./task-card/version-page";
import useAdminEvents from "../../../hooks/useAdminEvents";
import { categoryRefusal, publishErrorMessage } from "./task-card/task-category";
import { useCan } from "../../../common/utils/usePermissions";

// LF360-2030: why a save that adds an article was refused. The search offers
// only articles that qualify, so these are what changed while it was open.
const ARTICLE_REFUSAL_MESSAGES = {
  ARTICLE_SWITCHED_OFF:
    "An article you added has been switched off, so the task was not saved. Remove it and try again.",
  ARTICLE_NOT_AVAILABLE:
    "An article you added is no longer available, so the task was not saved. Remove it and try again.",
};

const DeleteModalComponent = ({ title, handleOk, handleCancel, isModalOpen }) => {
  return (
    <Modal
      title={title}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      okButtonProps={{ danger: true, "data-testid": "task-delete-confirm" }}
      okText="Delete"
      className="plan-page-action-modal"
    >
      <p>Are you sure you want to delete this task?</p>
    </Modal>
  );
};

const ALLOWED_STATUSES = new Set(["active", "pending", "inactive"]);

function parseStatusesFromSearch(search) {
  const sp = new URLSearchParams(search || "");

  const rawAll = sp.getAll("statuses").flatMap((v) => String(v).split(","));

  const statuses = rawAll
    .map((v) => v.trim())
    .filter(Boolean)
    .filter((v) => ALLOWED_STATUSES.has(v));

  return statuses.length ? statuses : null;
}

const TaskManagement = ({ mode }) => {
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [plans, setPlans] = useState([]);
  const [pages, setPages] = useState([]);
  const [isOpen, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTask, setSelectedTask] = useState({});
  const { t } = useTranslation();
  // LF360-2024: creating needs `content.edit` on the server.
  const canCreate = useCan("content.edit");
  const { openNotification } = useNotification();
  const [isOpenDeleteTaskModal, setOpenDeleteTaskModal] = useState();
  const { id } = useParams();
  const navigate = useProjectNavigate();
  const location = useLocation();

  const [filters, setFilters] = useState(() => {
    const statusesFromUrl = parseStatusesFromSearch(location.search);
    return statusesFromUrl ? { statuses: statusesFromUrl } : {};
  });

  const [searchValue, setSearchValue] = useState("");
  const [cohortsList, setCohortsList] = useState([]);
  const [sorting, setSorting] = useState({
    sortField: "updatedAt",
    sortOrder: "DESC",
  });
  const [showVersionPage, setShowVersionPage] = useState(false);
  const [isLoading, setLoading] = useState();
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const reqIdRef = useRef(0);
  const [searchParams] = useSearchParams();

  const formatCohortOptions = (cohorts = []) =>
    (cohorts || []).map((c) => ({ id: c.id, name: c.value }));

  const syncTaskCohortLabels = (task, cohortOptions) => {
    if (!task?.cohorts) return task;

    const cohortNameById = new Map(
      (cohortOptions || []).map((cohort) => [cohort.id, cohort.name])
    );

    return {
      ...task,
      cohorts: (task.cohorts || []).map((cohort) => {
        const id = cohort?.id ?? cohort?.value;
        const label = cohortNameById.get(id) || cohort?.label || cohort?.name || cohort?.value;

        return {
          ...cohort,
          id,
          value: label,
          label,
        };
      }),
    };
  };

  const mergeUniqueById = (prev, incoming) => {
    const seen = new Set(prev.map((i) => i.id));
    return prev.concat((incoming || []).filter((i) => !seen.has(i.id)));
  };

  const handleNavigate = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const openVersionInUrl = (taskId) => {
    const sp = new URLSearchParams(searchParams);
    sp.set("view", "version");

    navigate(`/content/tasks/${taskId}?${sp.toString()}`, { replace: true });
  };

  const closeVersionInUrl = (taskId) => {
    const sp = new URLSearchParams(searchParams);
    sp.delete("view");
    const qs = sp.toString();
    navigate(`/content/tasks/${taskId}${qs ? `?${qs}` : ""}`, { replace: true });
  };

  const setShowVersionPageWithUrl = (flag, taskIdArg) => {
    const taskId = taskIdArg || selectedTask?.id || id;
    setShowVersionPage(flag);
    if (!taskId) return;
    if (flag) openVersionInUrl(taskId);
    else closeVersionInUrl(taskId);
  };

  const [isToggleModalOpen, setToggleModalOpen] = useState(false);
  const [toggleItem, setToggleItem] = useState(null);

  const openToggleModal = (item) => {
    setToggleItem(item);
    setToggleModalOpen(true);
  };
  const closeToggleModal = () => {
    setToggleModalOpen(false);
    setToggleItem(null);
  };

  const handleToggleOk = async () => {
    if (!toggleItem) return;
    await toggleActive(toggleItem);
    closeToggleModal();
  };

  const handleToggleCancel = () => {
    closeToggleModal();
  };

  const outlet = useOutletContext();
  const setSidebarProps = outlet?.setSidebarProps ? outlet.setSidebarProps : () => {};

  // const handleDeselect = () => {
  //   setShowVersionPageWithUrl(false);
  // };

  const handleDeselect = async () => {
    const taskId = selectedTask?.id || id;

    if (taskId) {
      try {
        const resp = await ApiTaskManagement.getById(taskId);
        const freshTask = resp?.data?.task;
        if (freshTask) {
          setSelectedTask(freshTask);
        }
      } catch (err) {
        console.error("Failed to refresh selected task", err);
      }
    }

    await resetAndFetch(searchValue);
    setShowVersionPageWithUrl(false, taskId);
  };

  useEffect(() => {
    if (id) {
      // If we already have the correct task selected and the drawer is open, do nothing.
      if (selectedTask?.id === id && isOpen) {
        return;
      }
      const taskFromList = tasks.find((t) => t.id === id);
      if (taskFromList) {
        setSelectedTask(taskFromList);
        setOpen(true);
      } else {
        // If the task is not in the list (e.g., direct navigation), fetch it.
        ApiTaskManagement.getById(id)
          .then((resp) => {
            if (resp?.data?.task) {
              setSelectedTask(resp.data.task);
              setOpen(true);
            }
          })
          .catch((err) => console.error("Failed to fetch task by id", err));
      }
    } else {
      // If there is no ID in the URL, close the drawer.
      setOpen(false);
      setSelectedTask({});
    }
  }, [id, tasks]);

  useEffect(() => {
    const wantVersion = (searchParams.get("view") || "") === "version";
    if (wantVersion && !showVersionPage) setShowVersionPage(true);
    if (!wantVersion && showVersionPage) setShowVersionPage(false);
  }, [searchParams, showVersionPage]);

  useEffect(() => {
    if (showVersionPage) {
      setSidebarProps({
        backOnly: true,
        onBack: handleDeselect,
      });
    } else {
      setSidebarProps({ title: "Content", backOnly: false, onBack: undefined });
    }
    return () => {
      setSidebarProps({ title: "Content", backOnly: false, onBack: undefined });
    };
  }, [showVersionPage, setSidebarProps]);

  const createAndOpenDrawer = async () => {
    // setLoading(true);
    try {
      const response = await ApiTaskManagement.addTask();
      const newItem = response?.data?.dailyTask;

      setSelectedTask(newItem);

      handleNavigate(`/content/tasks/${newItem.id}/edit`);

      // await getTasks();
      await resetAndFetch(searchValue);
    } catch (error) {
      console.error("Error creating Task:", error);
      openNotification({
        type: "error",
        message: <p>{t(`task_management.notification.create_failed`)}</p>,
      });
    } finally {
      // setLoading(false);
    }
  };

  const cancelEditing = () => {
    handleNavigate(`/content/tasks/${selectedTask.id}`);
    setEditMode(false);
  };
  const closeTaskDrawer = () => {
    handleNavigate(`/content/tasks`);
    setEditMode(false);
    setOpen(false);
    setSelectedTask({});
  };

  const loadPage = async (nextPage, { replace = false, query = "" } = {}) => {
    if (isLoading && !replace) return;
    setLoading(true);
    try {
      const myReqId = ++reqIdRef.current;
      const { data } = await ApiTaskManagement.getTasks({
        query,
        ...filters,
        sortField: sorting.sortField,
        sortOrder: sorting.sortOrder,
        page: nextPage,
        // limit: DEFAULT_LIMIT,
      });
      if (myReqId !== reqIdRef.current) return;

      const incoming = data?.taskManagements || [];

      setTasks((prev) => (replace ? incoming : mergeUniqueById(prev, incoming)));

      if (data?.categories) setCategories(data.categories);
      if (data?.pages) setPages(data.pages);
      if (data?.plans) setPlans(data.plans);
      if (data?.cohorts) setCohortsList(data.cohorts.map((c) => ({ id: c.id, name: c.value })));

      setHasMore(Boolean(data?.hasMore));
      setPage(Number.isFinite(data?.page) ? data.page : nextPage);
    } catch (e) {
      console.log("Task list fetch error", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchNext = () => {
    if (!hasMore || isLoading) return;
    return loadPage(page + 1);
  };

  const resetAndFetch = async (query = "") => {
    setTasks([]);
    setPage(0);
    setHasMore(true);
    await loadPage(1, { replace: true, query });
  };

  const refreshCohorts = useCallback(async () => {
    try {
      const { data } = await ApiCohortManagement.getCohorts();
      const nextCohortsList = formatCohortOptions(data?.cohorts || []);

      setCohortsList(nextCohortsList);
      setTasks((prev) =>
        (prev || []).map((task) => syncTaskCohortLabels(task, nextCohortsList))
      );
      setSelectedTask((prev) =>
        prev?.id ? syncTaskCohortLabels(prev, nextCohortsList) : prev
      );
    } catch (error) {
      console.error("Failed to refresh cohorts after admin event", error);
    }
  }, []);

  useAdminEvents({
    onCohortChanged: refreshCohorts,
    onCategoryChanged: () => resetAndFetch(searchValue),
    onTaskChanged: (payload) => {
      const isInlineUpdate = payload?.action === "updated" && payload?.task;
      const nextTask = isInlineUpdate
        ? syncTaskCohortLabels(payload.task, cohortsList)
        : null;

      if (isInlineUpdate) {
        setTasks((prev) =>
          (prev || []).map((task) =>
            task.id === nextTask.id ? { ...task, ...nextTask } : task
          )
        );
      } else {
        resetAndFetch(searchValue);
      }

      setSelectedTask((prev) => {
        if (!prev?.id) return prev;

        const changedIds = [
          ...(payload?.deletedIds || []),
          payload?.id,
          payload?.activeCopyId,
          payload?.activeId,
        ].filter(Boolean);

        if (!changedIds.includes(prev.id)) return prev;

        if (payload?.action === "deleted" || payload?.action === "bulk_deleted") {
          closeTaskDrawer();
          return {};
        }

        if (payload?.action === "published" && payload?.activeCopyId === prev.id) {
          return prev;
        }

        return nextTask ? { ...prev, ...nextTask } : prev;
      });
    },
  });

  const getTasks = async (_deselect, query = "") => resetAndFetch(query);

  const handleTableChange = (pagination, filters, sorter) => {
    if (!sorter.order) {
      setSorting({ sortField: "updatedAt", sortOrder: "DESC" });
      return;
    }

    setSorting({
      sortField: sorter.field,
      sortOrder: sorter.order === "ascend" ? "ASC" : "DESC",
    });
  };

  const debouncedGetTasks = useCallback(
    debounce((q) => resetAndFetch(q), 400),
    [filters]
  );

  const handleUpdateSearchValue = (value) => {
    setSearchValue(value);
    debouncedGetTasks(value);
  };

  const headerProps = useMemo(
    () => ({
      title: "Task management",
      create: canCreate && {
        label: t("task_management.create"),
        onClick: () => createAndOpenDrawer(true),
      },
      search: {
        value: searchValue,
        onHandleChange: handleUpdateSearchValue,
      },
    }),
    [searchValue, isOpen, canCreate]
  );

  useEffect(() => {
    resetAndFetch(searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sorting]);

  useEffect(() => {
    if (!id && isOpen) {
      setOpen(false);
      setSelectedTask({});
    }
  }, [id, isOpen]);

  useEffect(() => {
    if (mode === "edit" || mode === "create") {
      setEditMode(true);
    } else {
      setEditMode(false);
    }

    if (mode === "create") {
      setOpen(true);
      setSelectedTask({});
    }
  }, [mode]);

  const isNewTaskValid = (task) => {
    let isValid = true;

    if (!task.title || task.title?.trim() === "") {
      openNotification({
        type: "error",
        message: <p>{t(`task_management.notification.no_title`)}</p>,
      });

      isValid = false;
    }

    if (!task.description || task.description?.trim() === "") {
      openNotification({
        type: "error",
        message: <p>{t(`task_management.notification.no_description`)}</p>,
      });

      isValid = false;
    }
    if (!task.avatar) {
      openNotification({
        type: "error",
        message: <p>{t(`task_management.notification.no_avatar`)}</p>,
      });

      isValid = false;
    }

    return isValid;
  };

  const normalizeTaskCohorts = (cohorts = []) =>
    (cohorts || []).map((cohort) => {
      const id = cohort?.id ?? cohort?.value;
      const label =
        cohort?.label ||
        cohort?.name ||
        cohortsList.find((item) => item.id === id)?.name ||
        cohort?.value;

      return {
        ...cohort,
        id,
        value: label,
        label,
      };
    });

  const createTask = (task) => {
    const isValid = isNewTaskValid(task);
    if (!isValid) return;

    setEditMode(false);
    ApiTaskManagement.addTask(task)
      .then((response) => {
        const createdTaskId = response.data.dailyTask.id;
        getTasks().then(() => {
          openNotification({
            type: "success",
            message: (
              <p>
                {t("task_management.notification.created", {
                  task: task.title,
                })}
              </p>
            ),
          });
          handleNavigate(`/content/tasks/${createdTaskId}`);
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t(
                `task_management.notification.${
                  e?.response?.data?.error?.code || "error"
                }`
              )}
            </p>
          ),
        });
      });
  };

  const updateTask = async (id, fields, { silent = false } = {}) => {
    try {
      const resp = await ApiTaskManagement.updateTask(id, fields);
      const serverTask = resp?.data?.taskManagement;
      const nextStatus = serverTask?.status ?? "pending";
      const nextStatusSummary = nextStatus === "pending" ? "pending" : undefined;
      const nextCohorts = fields.cohorts
        ? normalizeTaskCohorts(fields.cohorts)
        : undefined;
      const markVersionPending = (versions) =>
        (versions || []).map((version) =>
          version?.status === "activeCopy" || version?.status === "pending"
            ? { ...version, status: "pending" }
            : version
        );

      if (silent) {
        setTasks((prev) =>
          (prev || []).map((t) =>
            t.id === id
              ? {
                  ...t,
                  title: fields.title ?? t.title,
                  description: fields.description ?? t.description,
                  taskType: fields.taskType ?? t.taskType,
                  selfAssigned:
                    typeof fields.selfAssigned === "boolean"
                      ? fields.selfAssigned
                      : t.selfAssigned,
                  pages: fields.pages ?? t.pages,
                  cohorts: nextCohorts ?? t.cohorts,
                  avatar: fields.avatar ?? t.avatar,
                  avatarColor: fields.avatarColor ?? t.avatarColor,
                  middleColor: fields.middleColor ?? t.middleColor,
                  backgroundColor: fields.backgroundColor ?? t.backgroundColor,
                  categoryId: fields.categoryId ?? t.categoryId,
                  status: nextStatus,
                  statusSummary: nextStatusSummary ?? t.statusSummary,
                  DailyTaskVersions: markVersionPending(t.DailyTaskVersions),
                }
              : t
          )
        );
        setSelectedTask((prev) =>
          prev?.id === id
            ? {
                ...prev,
                title: fields.title ?? prev.title,
                description: fields.description ?? prev.description,
                taskType: fields.taskType ?? prev.taskType,
                selfAssigned:
                  typeof fields.selfAssigned === "boolean"
                    ? fields.selfAssigned
                    : prev.selfAssigned,
                pages: fields.pages ?? prev.pages,
                cohorts: nextCohorts ?? prev.cohorts,
                avatar: fields.avatar ?? prev.avatar,
                avatarColor: fields.avatarColor ?? prev.avatarColor,
                middleColor: fields.middleColor ?? prev.middleColor,
                backgroundColor: fields.backgroundColor ?? prev.backgroundColor,
                categoryId: fields.categoryId ?? prev.categoryId,
                status: nextStatus,
                statusSummary: nextStatusSummary ?? prev.statusSummary,
                DailyTaskVersions: markVersionPending(prev.DailyTaskVersions),
              }
            : prev
        );
        return;
      }

      if (!serverTask) {
        await resetAndFetch(searchValue);
        return;
      }

      await resetAndFetch(searchValue);
    } catch (e) {
      console.log(e);
      // LF360-2030: the refusals an admin can act on — an added article that
      // stopped qualifying after it was picked. Saving stays silent otherwise,
      // as before.
      const articleRefusal = ARTICLE_REFUSAL_MESSAGES[e?.response?.data?.error];
      if (articleRefusal) {
        openNotification?.({
          type: "error",
          message: <p>{articleRefusal}</p>,
        });
      }
    }
  };

  const [duplicatingTaskId, setDuplicatingTaskId] = useState(null);

  const handleDuplicateTask = async (task) => {
    if (!task?.id || duplicatingTaskId === task.id) {
      return;
    }

    try {
      setDuplicatingTaskId(task.id);

      await ApiTaskManagement.duplicateTask(task.id);

      openNotification?.({
        type: "success",
        message: <p>{`Duplicated task "${task.title}"`}</p>,
      });

      await resetAndFetch(searchValue);
    } catch (error) {
      console.error("Duplicate task error:", error);
      openNotification?.({
        type: "error",
        message: <p>Failed to duplicate task</p>,
      });
    } finally {
      setDuplicatingTaskId(null);
    }
  };

  const toggleActive = async (item) => {
    try {
      closeTaskDrawer();
      await ApiTaskManagement.toggleStatus(item.id);
      openNotification?.({
        type: "success",
        message: <p>Status changed</p>,
      });
      // await getTasks();
      await resetAndFetch(searchValue);
    } catch (err) {
      if (isReferencedError(err)) {
        openNotification?.({
          type: "error",
          message: <p>{t("common.referenced_delete_error")}</p>,
        });
      } else {
        // LF360-2011: activating a task whose live version has no category is
        // refused for the same reason publishing one is.
        openNotification?.({
          type: "error",
          message: <p>{categoryRefusal(err) || "Failed to change status"}</p>,
        });
      }
    }
  };

  const deleteTask = (id, title) => {
    closeTaskDrawer();
    ApiTaskManagement.deleteTask(id)
      .then(() => {
        // getTasks();
        resetAndFetch(searchValue);
      })
      .then(() => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t("task_management.notification.deleted", {
                task: title,
              })}
            </p>
          ),
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: (
            <p data-testid="task-delete-error">
              {t(
                `task_management.notification.${
                  e?.response?.data?.error?.code || "error"
                }`
              )}
            </p>
          ),
        });
      });
  };

  const handleSelectTask = (task) => {
    handleNavigate(`/content/tasks/${task.id}`);
    setSelectedTask(task);
    setOpen(true);
  };

  const toEditMode = () => {
    handleNavigate(`/content/tasks/${selectedTask.id}/edit`);
    setEditMode(true);
  };

  const openDeleteModal = () => {
    setOpenDeleteTaskModal(true);
  };

  const closeDeleteModal = () => {
    setOpenDeleteTaskModal(false);
  };

  const handleDeleteOk = () => {
    deleteTask(selectedTask.id, selectedTask.title);
    closeDeleteModal();
  };

  const handleDeleteCancel = () => {
    closeDeleteModal();
  };

  const updateFilterValue = (value) => setFilters(value);

  const taskTypes = [
    { id: "regular", name: "Regular" },
    { id: "survey", name: "Survey" },
  ];

  // ✅ той самий список статусів для дропдауну (UI) як всюди
  const statuses = [
    { id: "active", name: "Active" },
    { id: "pending", name: "Pending" },
    { id: "inactive", name: "Inactive" },
  ];

  const handlePublish = async (id) => {
    try {
      await ApiTaskManagement.publish(id);
      openNotification?.({
        type: "success",
        message: <p>Task has been published</p>,
      });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await resetAndFetch(searchValue);
    } catch (error) {
      console.error("Publish error:", error);
      openNotification?.({
        type: "error",
        // LF360-2011: the reason, when the server gives one we have words for.
        message: <p>{publishErrorMessage(error)}</p>,
      });
    }
  };

  const handleDiscard = async (id) => {
    try {
      await ApiTaskManagement.discard(id);
      openNotification?.({
        type: "success",
        message: <p>Changes discarded</p>,
      });
      await resetAndFetch(searchValue);
    } catch (error) {
      console.error("Discard error:", error);
      openNotification?.({
        type: "error",
        message: <p>Failed to discard changes</p>,
      });
    }
  };

  const handleBulkDeleteTasks = async (ids, { onDone } = {}) => {
    try {
      const resp = await ApiTaskManagement.deleteTasks(ids);
      const { deletedIds = [], failed = [] } = resp?.data || {};

      const failedIds = failed.map((f) => f.id);

      if (deletedIds.length) {
        const plural = deletedIds.length > 1;
        openNotification?.({
          type: "success",
          message: (
            <p>
              {deletedIds.length} {plural ? "items were" : "item was"} deleted
            </p>
          ),
        });
      }

      if (failedIds.length) {
        const plural = failedIds.length > 1;
        openNotification?.({
          type: "error",
          message: hasReferencedFailure(failed) ? (
            <p>{t("common.referenced_delete_error")}</p>
          ) : (
            <p>
              Unable to delete {failedIds.length} {plural ? "items" : "item"}
            </p>
          ),
        });
      }

      onDone?.({ failedIds });

      await resetAndFetch(searchValue);
    } catch (e) {
      console.error(e);

      openNotification?.({
        type: "error",
        message: <p>Failed to delete tasks</p>,
      });

      onDone?.({ failedIds: ids });
    }
  };

  return showVersionPage ? (
    <div style={{ padding: 24 }}>
      <VersionPage
        task={selectedTask}
        setShowVersionPage={(flag) => setShowVersionPageWithUrl(flag, selectedTask?.id)}
        handleDeselect={handleDeselect}
      />
    </div>
  ) : (
    <Body
      header={headerProps}
      filters={
        <DropdownFilters
          mode="controlled"
          filter={filters}
          filtersBy={[
            { name: "Cohort", id: "cohortIds", children: cohortsList },
            { name: "Category", id: "categoryIds", children: categories },
            { name: "Task type", id: "taskTypes", children: taskTypes },
            { name: "Status", id: "statuses", children: statuses }, // ✅ новий фільтр
            CREATED_VIA_FILTER,
          ]}
          updateFilterValue={updateFilterValue}
        />
      }
    >
      <TaskTable
        onChange={handleTableChange}
        sorting={sorting}
        data={tasks}
        onHandleRowClick={handleSelectTask}
        isLoading={isLoading}
        hasMore={hasMore}
        fetchData={fetchNext}
        onBulkDelete={handleBulkDeleteTasks}
      />
      <TaskCard
        task={selectedTask}
        categories={categories}
        plans={plans}
        cohorts={cohortsList}
        isOpen={isOpen}
        close={closeTaskDrawer}
        editMode={editMode}
        toEditMode={toEditMode}
        createTask={createTask}
        updateTask={updateTask}
        deleteTask={openDeleteModal}
        toggleActive={openToggleModal}
        cancelEditing={cancelEditing}
        pages={pages}
        setShowVersionPage={(flag) => setShowVersionPageWithUrl(flag, selectedTask?.id)}
        onDiscard={handleDiscard}
        onPublish={handlePublish}
        onDuplicate={handleDuplicateTask}
      />
      <DeleteModalComponent
        isModalOpen={isOpenDeleteTaskModal}
        handleOk={handleDeleteOk}
        handleCancel={handleDeleteCancel}
        title={selectedTask.title}
      />
      <Modal
        title={toggleItem?.title}
        open={isToggleModalOpen}
        onOk={handleToggleOk}
        onCancel={handleToggleCancel}
        okButtonProps={{ danger: !!toggleItem?.isActive }}
        okText={toggleItem?.isActive ? "Deactivate" : "Activate"}
        className="plan-page-action-modal"
      >
        <p>
          Are you sure you want to {toggleItem?.isActive ? "deactivate" : "activate"} this task?
        </p>
      </Modal>
    </Body>
  );
};

export default TaskManagement;
